import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const FollowUpOptions = ({ onClose, resultsData }) => {
  const [selectedOption, setSelectedOption] = useState('newsletter');
  const [emailAddress, setEmailAddress] = useState('');
  const [reminderFrequency, setReminderFrequency] = useState('monthly');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const followUpOptions = [
    {
      id: 'newsletter',
      title: 'Wellness Newsletter',
      description: 'Receive monthly wellness tips and mental health resources',
      icon: 'Mail',
      features: ['Monthly wellness tips', 'Mental health resources', 'Community stories', 'Expert advice']
    },
    {
      id: 'reminders',
      title: 'Progress Reminders',
      description: 'Get gentle reminders to check in on your wellness goals',
      icon: 'Bell',
      features: ['Customizable frequency', 'Goal tracking prompts', 'Motivational messages', 'Progress celebrations']
    },
    {
      id: 'reassessment',
      title: 'Reassessment Scheduling',
      description: 'Schedule follow-up assessments to track your progress',
      icon: 'Calendar',
      features: ['Automated scheduling', 'Progress comparison', 'Trend analysis', 'Personalized insights']
    }
  ];

  const reminderOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Every 2 weeks' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  const handleSubmit = async () => {
    if (!emailAddress) return;
    
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
      
      // Auto close after success
      setTimeout(() => {
        onClose();
      }, 2000);
    }, 2000);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-70 flex items-center justify-center p-4">
        <div className="bg-surface rounded-xl shadow-modal border border-border max-w-md w-full">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Check" size={32} color="var(--color-success)" />
            </div>
            <h3 className="text-lg font-heading font-semibold text-text-primary mb-2">
              You're All Set!
            </h3>
            <p className="text-text-secondary mb-4">
              We'll send you {selectedOption === 'newsletter' ? 'wellness tips' : selectedOption === 'reminders' ? 'progress reminders' : 'reassessment notifications'} at {emailAddress}
            </p>
            <p className="text-sm text-text-secondary">
              You can update your preferences anytime by clicking the link in any email.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-70 flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl shadow-modal border border-border max-w-2xl w-full max-h-96 overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-heading font-semibold text-text-primary">
              Stay Connected with Your Wellness Journey
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-text-secondary hover:text-text-primary transition-colors duration-150"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="space-y-6">
            <p className="text-text-secondary">
              Choose how you'd like to continue your wellness journey and stay motivated with your goals.
            </p>

            {/* Follow-up Options */}
            <div className="space-y-4">
              {followUpOptions.map((option) => (
                <label key={option.id} className="block cursor-pointer">
                  <div className={`p-4 border rounded-lg transition-all duration-200 ${
                    selectedOption === option.id 
                      ? 'border-primary bg-primary-50' :'border-border hover:border-primary-200'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <input
                        type="radio"
                        name="followUpOption"
                        value={option.id}
                        checked={selectedOption === option.id}
                        onChange={(e) => setSelectedOption(e.target.value)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Icon name={option.icon} size={20} color="var(--color-primary)" />
                          <h4 className="font-medium text-text-primary">{option.title}</h4>
                        </div>
                        <p className="text-sm text-text-secondary mb-3">{option.description}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {option.features.map((feature, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                              <span className="text-xs text-text-secondary">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="Enter your email address"
                className="form-input"
              />
            </div>

            {/* Frequency Selection for Reminders */}
            {selectedOption === 'reminders' && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Reminder Frequency
                </label>
                <select
                  value={reminderFrequency}
                  onChange={(e) => setReminderFrequency(e.target.value)}
                  className="form-input"
                >
                  {reminderOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Next Assessment Date for Reassessment */}
            {selectedOption === 'reassessment' && (
              <div className="bg-accent-50 border border-accent-100 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Icon name="Calendar" size={16} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
                  <div>
                    <h5 className="font-medium text-accent-600 mb-1">Recommended Timeline</h5>
                    <p className="text-sm text-accent-600">
                      Based on your current results, we recommend retaking the assessment in 3 months to track your progress and adjust recommendations.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Notice */}
            <div className="bg-surface-secondary rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon name="Shield" size={16} color="var(--color-text-secondary)" className="mt-0.5 flex-shrink-0" />
                <div>
                  <h5 className="font-medium text-text-primary mb-1">Privacy Promise</h5>
                  <p className="text-sm text-text-secondary">
                    We'll only send you the content you've requested. You can unsubscribe anytime, and we never share your information with third parties.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-surface border border-border rounded-lg text-text-primary font-medium hover:bg-surface-secondary transition-all duration-150"
              >
                Skip for Now
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !emailAddress}
                className="flex-1 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    <span>Setting Up...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="Check" size={16} />
                    <span>Get Started</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FollowUpOptions;